import { createStore, createEvent, createEffect, sample } from 'effector';
import { axiosClient } from '@nx-frontend/shared/api';
import { membersToState, setMembersStateEvent } from '@nx-frontend/shared/store/org-members';
import { setUserStateEvent, usersToState } from '@nx-frontend/shared/store/users-store';
import { OrgMember, OrgMemberRole } from '@nx-frontend/shared/types';
import { Member } from '@nx-frontend/shared/types';
import { getBoxFileUrl } from '@nx-frontend/shared/utils';

interface MembersState {
  entities: Record<string, Member>;
  isFetching: boolean;
  isLoaded: boolean;
  count: number;
}

export const membersInitalState: MembersState = {
  entities: {},
  isFetching: false,
  isLoaded: false,
  count: 0,
};

export const fetchMembersEvent = createEvent<{ orgId: string }>();
export const startFetchMembersEvent = createEvent();

export const fetchMembersFx = createEffect(async ({ orgId }: { orgId: string }) => {
  const { data } = await axiosClient.get<Member[]>(`/v2/api/orgs/${orgId}/membersWithOwner`);
  return data;
});

export const $members = createStore(membersInitalState);

$members.on(startFetchMembersEvent, (_) => {
  return {
    ..._,
    isFetching: true,
  };
});

/**
 * When fetchMembersEvent will called, it will triggered:
 * - startFetchMembersEvent — to set isFetching to true
 * - fetchMembersFx — to fetch members from API
 */
sample({
  source: $members,
  clock: fetchMembersEvent,
  filter: (state) => !(state.isFetching || state.isLoaded),
  fn: (_, params) => params,
  target: [startFetchMembersEvent, fetchMembersFx],
});

sample({
  source: [$members],
  clock: fetchMembersFx.doneData,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  fn: ([_], members) => {
    const users = members.map(({ user, avatar }) => {
      const avatarUrl =
        getBoxFileUrl(avatar?.storedFileUUID ?? '', {
          width: 64,
          height: 64,
        }) ?? '';

      return {
        ...user,
        avatar: avatarUrl,
      };
    });
    return usersToState(users);
  },
  target: [setUserStateEvent],
});
sample({
  source: $members,
  clock: fetchMembersFx.doneData,
  fn: (_, members) => {
    const orgMembers: OrgMember[] = members.map((member) => {
      return {
        globalId: member.globalId,
        orgId: member.orgId,
        userId: member.userId,
        createdAt: member.createdAt,
        updatedAt: member.updatedAt,
        role: member.role as OrgMemberRole,
        disabled: member.disabled,
        expiresAt: member.expiresAt,
        memberTTL: member.memberTTL,
      };
    });
    return membersToState(orgMembers);
  },
  target: [setMembersStateEvent],
});

$members.on(fetchMembersFx.doneData, (_, members) => {
  const membersMap: Record<number, Member> = members.reduce((acc, member) => {
    acc[member.userId] = member;
    return acc;
  }, {} as Record<number, Member>);

  return {
    entities: membersMap,
    isFetching: false,
    isLoaded: true,
    count: members.length,
  };
});
